import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  IconButton,
  useTheme,
  Fade,
  Slide
} from '@mui/material';
import {
  GetApp as InstallIcon,
  PhoneIphone as IOSIcon,
  Language as BrowserIcon,
  Close as CloseIcon,
  Smartphone as AndroidIcon
} from '@mui/icons-material';
import {
  getDeviceInfo,
  dismissMobileFirstScreen,
  supportsAutomaticPWAInstall
} from '../utils/deviceDetection';
import { showInstallPrompt, isDeferredPromptAvailable } from '../utils/serviceWorkerRegistration';
import IOSInstallInstructions from './IOSInstallInstructions';
import AndroidInstallInstructions from './AndroidInstallInstructions';
import Logo from './Logo';

interface PWAMobileFirstScreenProps {
  onContinueInBrowser: () => void;
  onInstallComplete?: () => void;
}

const PWAMobileFirstScreen: React.FC<PWAMobileFirstScreenProps> = ({
  onContinueInBrowser,
  onInstallComplete
}) => {
  const theme = useTheme();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showAndroidInstructions, setShowAndroidInstructions] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [deviceInfo] = useState(getDeviceInfo());
  const [installPromptAvailable, setInstallPromptAvailable] = useState(false);

  useEffect(() => {
    // Listen for PWA installation completion
    const handleInstalled = () => {
      setIsInstalling(false);
      dismissMobileFirstScreen();
      if (onInstallComplete) {
        onInstallComplete();
      }
    };

    // Listen for install prompt availability
    const handleInstallAvailable = () => {
      setInstallPromptAvailable(true);
      console.log('PWA install prompt is now available');
    };

    window.addEventListener('pwaInstalled', handleInstalled);
    window.addEventListener('pwaInstallAvailable', handleInstallAvailable);

    return () => {
      window.removeEventListener('pwaInstalled', handleInstalled);
      window.removeEventListener('pwaInstallAvailable', handleInstallAvailable);
    };
  }, [onInstallComplete]);

  const handleAndroidInstall = async () => {
    console.log('Android install clicked');
    console.log('Device info:', deviceInfo);
    console.log('Install prompt available:', installPromptAvailable);
    console.log('Deferred prompt available:', isDeferredPromptAvailable());
    console.log('Supports automatic PWA install:', supportsAutomaticPWAInstall());

    if (deviceInfo.isAndroid) {
      setIsInstalling(true);
      try {
        // Check if deferred prompt is available
        if (isDeferredPromptAvailable()) {
          console.log('Showing deferred install prompt');
          const result = await showInstallPrompt();
          console.log('Install prompt result:', result);
          setIsInstalling(false);
        } else {
          console.log('No deferred prompt available, showing manual instructions');
          setIsInstalling(false);
          setShowAndroidInstructions(true);
        }
      } catch (error) {
        console.error('Install failed:', error);
        setIsInstalling(false);
        setShowAndroidInstructions(true);
      }
    } else {
      console.log('Not an Android device');
      setIsInstalling(false);
    }
  };

  const handleIOSInstall = () => {
    setShowIOSInstructions(true);
  };

  const handleContinueInBrowser = () => {
    dismissMobileFirstScreen();
    onContinueInBrowser();
  };

  const handleSkip = () => {
    dismissMobileFirstScreen();
    onContinueInBrowser();
  };

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }}
        />

        {/* Skip Button */}
        <IconButton
          onClick={handleSkip}
          sx={{
            position: 'absolute',
            top: { xs: 16, sm: 20 },
            right: { xs: 16, sm: 20 },
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Container maxWidth="sm" sx={{ px: 2 }}>
          <Slide direction="up" in timeout={800}>
            <Card
              elevation={8}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: 'white',
                mx: 'auto',
                maxWidth: 400
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                {/* Logo */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ transform: { xs: 'scale(0.8)', sm: 'scale(1)' } }}>
                    <Logo />
                  </Box>
                </Box>

                {/* Welcome Text */}
                <Typography
                  variant="h4"
                  component="h1"
                  fontWeight="bold"
                  color="primary"
                  sx={{
                    mb: 1.5,
                    fontSize: { xs: '1.5rem', sm: '2rem' }
                  }}
                >
                  Welcome to TruckSight
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    mb: 3,
                    lineHeight: 1.5,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    px: 1
                  }}
                >
                  Get the best experience with our mobile app. Install TruckSight
                  for faster loading, offline access, and push notifications.
                </Typography>

                {/* Installation Options */}
                <Box sx={{ mb: 2.5 }}>
                  {true && (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={isInstalling ? null : <AndroidIcon />}
                      onClick={handleAndroidInstall}
                      disabled={isInstalling}
                      sx={{
                        mb: 1.5,
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        fontWeight: 'medium',
                        borderRadius: 2,
                        textTransform: 'none'
                      }}
                    >
                      {isInstalling ? 'Installing...' : 'Install App (Android)'}
                    </Button>
                  )}

                  {true && (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<IOSIcon />}
                      onClick={handleIOSInstall}
                      sx={{
                        mb: 1.5,
                        py: { xs: 1.5, sm: 2 },
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        fontWeight: 'medium',
                        borderRadius: 2,
                        textTransform: 'none',
                        backgroundColor: '#007AFF',
                        '&:hover': {
                          backgroundColor: '#0051D4'
                        }
                      }}
                    >
                      Install App (iOS)
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    startIcon={<BrowserIcon />}
                    onClick={handleContinueInBrowser}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      fontWeight: 'medium',
                      borderRadius: 2,
                      textTransform: 'none',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2
                      }
                    }}
                  >
                    Continue in Browser
                  </Button>
                </Box>

                {/* Benefits */}
                <Box
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    mt: 2
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="primary"
                    sx={{ mb: 1.5, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}
                  >
                    App Benefits:
                  </Typography>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ mb: 0.8, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      ⚡ Faster loading times
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.8, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      📱 Native app experience
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.8, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      🔔 Push notifications
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      🌐 Works offline
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Container>

        {/* iOS Installation Instructions Modal */}
        <IOSInstallInstructions
          open={showIOSInstructions}
          onClose={() => setShowIOSInstructions(false)}
        />

        {/* Android Installation Instructions Modal */}
        <AndroidInstallInstructions
          open={showAndroidInstructions}
          onClose={() => setShowAndroidInstructions(false)}
        />
      </Box>
    </Fade>
  );
};

export default PWAMobileFirstScreen;
